import { z } from "zod";
import { MyPropertyDetailSchema } from "./landlord";

/**
 * The admin CRM's API (/v1/admin). Staff only: the gateway requires a second
 * factor (aal2) and the Core API checks the caller is an admin.
 *
 * Times are ISO strings. Every list is capped, newest or oldest first as the
 * queue needs; the CRM never asks for "everything".
 */

const iso = z.string();
const id = z.uuid();

/* ── Overview ── */
export const OverviewSchema = z.object({
  pendingLandlords: z.number().int(),
  openReports: z.number().int(),
  activeListings: z.number().int(),
  lookingTenants: z.number().int(),
  suspendedUsers: z.number().int(),
  pendingListingVerifications: z.number().int(),
  openLeads: z.number().int(),
  email: z.object({ from: z.string(), hasApiKey: z.boolean(), adminNotifyEmail: z.boolean() }),
});
export type Overview = z.infer<typeof OverviewSchema>;

/* ── Analytics ── */
const DailySchema = z.array(z.object({ day: z.string(), count: z.number().int() }));
export const AnalyticsSchema = z.object({
  users: z.number().int(),
  activeListings: z.number().int(),
  lookingTenants: z.number().int(),
  totalBookings: z.number().int(),
  bookingsByStatus: z.record(z.string(), z.number().int()),
  signups14d: DailySchema,
  requests14d: DailySchema,
  approvalRate: z.number().nullable(),
  avgHoursToApproval: z.number().nullable(),
  fillRate: z.number().nullable(),
});
export type Analytics = z.infer<typeof AnalyticsSchema>;

/* ── Reports ── */
export const ReportTargetSchema = z.object({
  label: z.string(),
  /** A path on the website, when the target has a page. */
  href: z.string().nullable(),
  /** The account to suspend if it comes to that. */
  ownerId: id.nullable(),
  propertyId: id.nullable(),
  reviewId: id.nullable(),
  ownerStatus: z.enum(["ACTIVE", "SUSPENDED"]).nullable(),
});
export const AdminReportSchema = z.object({
  id,
  targetType: z.string(),
  targetId: id,
  reason: z.string(),
  details: z.string().nullable(),
  status: z.enum(["OPEN", "RESOLVED", "DISMISSED"]),
  createdAt: iso,
  resolvedAt: iso.nullable(),
  reporterEmail: z.string().nullable(),
  resolvedByEmail: z.string().nullable(),
  /** Null when the target has since been deleted. */
  target: ReportTargetSchema.nullable(),
});
export type AdminReport = z.infer<typeof AdminReportSchema>;
export const ReportsSchema = z.array(AdminReportSchema);
export const ResolveReportBodySchema = z.strictObject({ status: z.enum(["RESOLVED", "DISMISSED"]) });

/* ── Moderation actions ── */
export const UserStatusBodySchema = z.strictObject({ status: z.enum(["ACTIVE", "SUSPENDED"]) });
export const ReviewStatusBodySchema = z.strictObject({ status: z.enum(["PUBLISHED", "PENDING", "REMOVED"]) });
export const FeaturedBodySchema = z.strictObject({ featured: z.boolean(), days: z.number().int().min(1).max(365).optional() });
export const EmailTestReplySchema = z.object({ ok: z.boolean(), message: z.string() });

/* ── Verifications ── */
export const PendingLandlordSchema = z.object({
  landlordProfileId: id,
  userId: id,
  displayName: z.string().nullable(),
  companyName: z.string().nullable(),
  fullName: z.string().nullable(),
  email: z.string(),
  phone: z.string().nullable(),
  phoneVerified: z.boolean(),
  emailVerified: z.boolean(),
  joinedAt: iso,
  requestedAt: iso.nullable(),
  listings: z.array(z.object({ id, title: z.string(), status: z.string(), city: z.string() })),
});
export const VerificationsSchema = z.object({
  pendingLandlords: z.array(PendingLandlordSchema),
  verifiedLandlords: z.array(z.object({ landlordProfileId: id, fullName: z.string().nullable(), email: z.string(), reviewedAt: iso.nullable() })),
  pendingListings: z.array(
    z.object({
      verificationId: id,
      propertyId: id,
      title: z.string(),
      slug: z.string(),
      city: z.string(),
      province: z.string(),
      submittedBy: z.string().nullable(),
      requestedAt: iso,
    }),
  ),
  verifiedListings: z.array(z.object({ propertyId: id, title: z.string(), slug: z.string(), verifiedAt: iso.nullable() })),
  stayConfirmations: z.array(
    z.object({ id, title: z.string(), slug: z.string(), by: z.string().nullable(), matchesListing: z.boolean().nullable(), notes: z.string().nullable(), createdAt: iso }),
  ),
});
export type Verifications = z.infer<typeof VerificationsSchema>;
export const DecisionBodySchema = z.strictObject({ approve: z.boolean(), notes: z.string().trim().max(1000).optional() });
export const RevokeBodySchema = z.strictObject({ notes: z.string().trim().max(500).optional() });
export const RevokeReplySchema = z.object({ pausedListings: z.number().int(), stillVerified: z.boolean() });

/* ── People ── */
export const AdminUserSchema = z.object({
  id,
  email: z.string(),
  fullName: z.string().nullable(),
  status: z.enum(["ACTIVE", "SUSPENDED"]),
  isAdmin: z.boolean(),
  activeRole: z.enum(["LANDLORD", "TENANT"]),
  isLandlord: z.boolean(),
  verifiedLandlord: z.boolean(),
  listings: z.number().int(),
  createdAt: iso,
  lastActiveAt: iso.nullable(),
});
export type AdminUser = z.infer<typeof AdminUserSchema>;
export const UsersQuerySchema = z.object({ q: z.string().trim().max(200).optional() });
export const UsersSchema = z.array(AdminUserSchema);

/** Why a staff action was refused, as the error `code`. */
export const AdminErrorCodes = ["not_found", "forbidden", "already_decided", "notes_required", "not_verified", "self", "rate_limited", "invalid_input", "has_bookings"] as const;

/* ── CRM leads ── */
export const LeadKindSchema = z.enum(["LANDLORD", "AGENT", "HOUSING_OFFICE", "OTHER"]);
export const LeadStatusSchema = z.enum(["NEW", "CONTACTED", "REPLIED", "INTERESTED", "SIGNED_UP", "NOT_INTERESTED"]);
export const LeadActivityKindSchema = z.enum(["NOTE", "EMAIL", "CALL", "MESSAGE", "STATUS"]);

const text = (max: number) => z.string().trim().max(max);
/** Fields a person can edit. Empty strings are stored as "not set". */
const LeadFields = {
  kind: LeadKindSchema,
  name: text(200).min(1),
  company: text(200).optional(),
  email: z.union([z.literal(""), z.email().max(200)]).optional(),
  phone: text(60).optional(),
  website: text(300).optional(),
  area: text(120).optional(),
  source: text(120).optional(),
  listingUrl: text(500).optional(),
  status: LeadStatusSchema,
  notes: text(4000).optional(),
  nextFollowUpAt: z.iso.date().nullable().optional(),
};

export const LeadSchema = z.object({
  id,
  kind: LeadKindSchema,
  name: z.string(),
  company: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  website: z.string().nullable(),
  area: z.string().nullable(),
  source: z.string().nullable(),
  listingUrl: z.string().nullable(),
  status: LeadStatusSchema,
  notes: z.string().nullable(),
  nextFollowUpAt: z.string().nullable(),
  lastContactedAt: iso.nullable(),
  ownerEmail: z.string().nullable(),
  userId: id.nullable(),
  createdAt: iso,
  updatedAt: iso,
});
export type Lead = z.infer<typeof LeadSchema>;
export const LeadsSchema = z.array(LeadSchema);
export const LeadsQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
  status: LeadStatusSchema.optional(),
  kind: LeadKindSchema.optional(),
  /** "1": only leads whose follow-up date has come. */
  due: z.enum(["1"]).optional(),
});

export const CreateLeadBodySchema = z.strictObject({ ...LeadFields, status: LeadStatusSchema.default("NEW"), kind: LeadKindSchema.default("LANDLORD") });
export type CreateLeadBody = z.infer<typeof CreateLeadBodySchema>;
export const UpdateLeadBodySchema = z.strictObject(Object.fromEntries(Object.entries(LeadFields).map(([k, v]) => [k, v.optional()])) as { [K in keyof typeof LeadFields]: z.ZodOptional<(typeof LeadFields)[K]> });
export type UpdateLeadBody = z.infer<typeof UpdateLeadBodySchema>;

export const LeadActivitySchema = z.object({ id, kind: LeadActivityKindSchema, body: z.string(), authorEmail: z.string().nullable(), createdAt: iso });
export const LeadDetailSchema = LeadSchema.extend({ activities: z.array(LeadActivitySchema) });
export type LeadDetail = z.infer<typeof LeadDetailSchema>;
export const AddActivityBodySchema = z.strictObject({ kind: LeadActivityKindSchema.exclude(["STATUS"]), body: text(4000).min(1) });

/** Bulk import, e.g. from the outreach tracker. A row whose email (or name and phone) is already a lead is skipped. */
export const ImportLeadsBodySchema = z.strictObject({ leads: z.array(CreateLeadBodySchema).min(1).max(500) });
export const ImportLeadsReplySchema = z.object({ created: z.number().int(), skipped: z.number().int() });

/* ── Listings (any listing, any owner) ── */
export const AdminListingStatusSchema = z.enum(["DRAFT", "PENDING_REVIEW", "ACTIVE", "PAUSED", "ARCHIVED"]);
export const AdminListingsQuerySchema = z.object({
  /** Title, city, slug, or the owner's name or email. */
  q: z.string().trim().max(200).optional(),
  status: AdminListingStatusSchema.optional(),
});
const OwnerSchema = z.object({
  id,
  email: z.string(),
  fullName: z.string().nullable(),
  verifiedLandlord: z.boolean(),
  status: z.enum(["ACTIVE", "SUSPENDED"]),
});
export const AdminListingRowSchema = z.object({
  id,
  title: z.string(),
  slug: z.string(),
  city: z.string(),
  province: z.string(),
  status: AdminListingStatusSchema,
  listingType: z.enum(["BY_THE_BED", "ENTIRE_UNIT"]),
  isFeatured: z.boolean(),
  verificationStatus: z.enum(["PENDING", "APPROVED", "REJECTED"]),
  totalSlots: z.number().int(),
  availableSlots: z.number().int(),
  /** The lowest all-in monthly price, in cents. Null with no rooms. */
  fromCents: z.number().int().nullable(),
  photos: z.number().int(),
  owner: OwnerSchema,
  createdAt: iso,
  updatedAt: iso,
});
export type AdminListingRow = z.infer<typeof AdminListingRowSchema>;
export const AdminListingsSchema = z.array(AdminListingRowSchema);

/** Everything the owner's edit page has, plus who owns it and how it is promoted. */
export const AdminListingSchema = MyPropertyDetailSchema.extend({
  owner: OwnerSchema,
  isFeatured: z.boolean(),
  featuredUntil: iso.nullable(),
  /** Bookings of any status, ever. A listing with any cannot be deleted, only archived. */
  bookings: z.number().int(),
  createdAt: iso,
});
export type AdminListing = z.infer<typeof AdminListingSchema>;

/** Staff may also move a listing back to draft. Publishing keeps the owner's rules (a verified owner, no overdue requests). */
export const AdminListingStatusBodySchema = z.strictObject({ status: z.enum(["DRAFT", "ACTIVE", "PAUSED", "ARCHIVED"]) });
